/**
 * Small set of controlled form primitives shared by the Create* forms.
 *
 * Intentionally thin wrappers over native inputs so the forms stay
 * framework-light and match the Tailwind styling already used elsewhere
 * in the admin portal (see RequestApprovalForm). Field state is kept as
 * strings by the caller and parsed at submit time — this sidesteps the
 * NaN / empty-string fiddliness of controlled numeric inputs and lets
 * each form validate against the matching Zod DTO in one place.
 */
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";
import { ApiError } from "@dfp/api-client";
import { cn } from "../lib/utils.js";

const controlClass =
  "mt-1.5 block w-full rounded-xl border border-simuflex-line bg-white px-3.5 py-2.5 text-sm text-simuflex-navy placeholder:text-simuflex-slate/75 shadow-sm focus:border-simuflex-navy focus:outline-none focus:ring-2 focus:ring-simuflex-navy/15 transition-all duration-200";

export function Field({
  label,
  hint,
  children,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="text-[11px] font-bold text-simuflex-navy/80 uppercase tracking-wider">
        {label}
      </span>
      {hint ? <span className="ml-1 text-[11px] text-simuflex-slate">· {hint}</span> : null}
      {children}
    </label>
  );
}

export function TextInput({
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return <input className={cn(controlClass, className)} {...props} />;
}

export function Select({
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>): JSX.Element {
  return (
    <select className={cn(controlClass, className)} {...props}>
      {children}
    </select>
  );
}

export function FormError({ message }: { readonly message: string | null }): JSX.Element | null {
  if (!message) return null;
  return (
    <div className="rounded-xl bg-simuflex-orange/10 border border-simuflex-orange/20 px-4 py-3 text-xs font-semibold text-simuflex-orange leading-normal animate-fade-in">
      {message}
    </div>
  );
}

/** Normalise an unknown thrown value into a human-readable form message. */
export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return err.problem.message ?? err.problem.detail ?? err.problem.title ?? "Request rejected";
  }
  return "Network error";
}
